/**
 * Kit — what the character carries, and what it does to the army behind him.
 *
 * Three rules shape this file, and the third is the one that matters.
 *
 * **Every value here is a delta, per level.** `{ unitDamage: 0.04 }` means "+4%
 * army damage for each level the item has", and `{ archerRange: 0.35 }` means
 * "+0.35 tiles per level". Which of those two readings applies is *not* decided
 * here: `defaultMods()` in `src/sim/world.ts` is the authority on whether a
 * modifier key multiplies or adds, and `src/sim/systems/items.ts` reads it. This
 * file is not allowed to import that — `src/data` may only see `src/sim/types.ts`
 * — and it does not need to. Writing the delta and letting the simulation
 * interpret it is what keeps one authority instead of two.
 *
 * **A negative delta on a multiplier key is a discount.** `buildCost: -0.03` is
 * 3% cheaper per level. The four "lower is better" keys — `buildCost`,
 * `grainUpkeep`, `powerCooldown`, `siegeResist`, `reinforceCost` — all read this
 * way, and the fold clamps the resulting factor so no amount of levels can take
 * one to zero.
 *
 * **Items are not relics.** A relic is a decision made once, permanently, about
 * what the realm becomes. An item is a thing on a cart: it can be swapped, it
 * can be melted down, and it grows with what you spend on it. So the numbers
 * here are deliberately *smaller* than a relic's — a level-one item is a
 * rounding error, and a level-twenty one is a plan. That is the grinder's shape:
 * the item you found at wave 4 is still the item you are carrying at wave 90,
 * because you have been feeding it since.
 *
 * The register is `relics.ts`. Nothing here is a Sword of Might.
 */

/**
 * Where a thing is worn.
 *
 * Five, and the split down the middle is the design. **Weapon and Armour are the
 * hero's own** — they carry a personal effect on top of the army-wide one,
 * because a sword and a harness are things one man holds and one man hides
 * behind. **Banner, Seal and Charm are the army's** — a standard is carried in
 * front of a line, a seal is pressed on an order somebody else reads, a charm is
 * a superstition a thousand men share. None of those three has any personal
 * effect at all, and that is the honest answer to "what does the hero get out of
 * it": nothing. The army does.
 */
export const ItemSlot = {
  Weapon: 0,
  Armour: 1,
  Banner: 2,
  Seal: 3,
  Charm: 4,
} as const;
export type ItemSlotId = (typeof ItemSlot)[keyof typeof ItemSlot];

export const ITEM_SLOT_COUNT = 5;

export const ITEM_SLOT_NAMES: readonly string[] = ['Weapon', 'Armour', 'Banner', 'Seal', 'Charm'];

/** What an empty slot says about itself. */
export const ITEM_SLOT_EMPTY: readonly string[] = [
  'Bare hands.',
  'Whatever he slept in.',
  'No colours.',
  'No warrant.',
  'Nothing worth carrying.',
];

/** 1 plain, 2 kept, 3 grim, 4 buried. Drives the drop weighting and the price. */
export const ITEM_RARITY_NAMES: readonly string[] = ['', 'Plain', 'Kept', 'Grim', 'Buried'];

export interface ItemDef {
  id: string;
  name: string;
  flavour: string;
  /** Exact mechanical text, stated per level. Tooltips are information-first. */
  rules: string;
  slot: number;
  /** 1..4. Higher is rarer, dearer to reforge, and worth more melted down. */
  rarity: number;
  /** Earliest wave this may drop. */
  minWave: number;
  /**
   * Army-wide, folded into `world.mods`. Per level, as a delta — see the header.
   *
   * `willMax` is deliberately absent from every item in this file and refused by
   * the fold. It is the one modifier with a second home: `world.willMax` is a
   * separate field that relics bump alongside the bag, and there is no way to
   * take that back off when the item comes off again without either storing a
   * ledger of who granted what or dividing a float. Items do not touch it.
   */
  mods: Partial<Record<string, number>>;
  /** Personal: added to the hero's own damage multiplier, per level. */
  heroDamage?: number;
  /** Personal: the hero's own body shrugs off this much more, per level. */
  heroWard?: number;
}

function it(
  id: string,
  name: string,
  flavour: string,
  rules: string,
  slot: number,
  rarity: number,
  minWave: number,
  mods: Partial<Record<string, number>>,
  extra: Partial<ItemDef> = {},
): ItemDef {
  return { id, name, flavour, rules, slot, rarity, minWave, mods, ...extra };
}

export const ITEMS: ItemDef[] = [
  // ── Weapon ───────────────────────────────────────────────────────────────
  it(
    'barrow_edge',
    'Barrow Edge',
    'Dug up rather than forged. It came out of the ground already sharp, which nobody wants explained.',
    'Army damage +4% per level. The hero himself strikes 6% harder per level.',
    ItemSlot.Weapon,
    1,
    1,
    { unitDamage: 0.04 },
    { heroDamage: 0.06 },
  ),
  it(
    'notched_falchion',
    'Notched Falchion',
    'Twenty-one notches in the spine. The twenty-second is where the edge used to be.',
    'Army attack speed +3% per level. The hero strikes 4% harder per level.',
    ItemSlot.Weapon,
    1,
    1,
    { attackSpeed: 0.03 },
    { heroDamage: 0.04 },
  ),
  it(
    'gravebiter',
    'Gravebiter',
    'It takes a tithe on the way through, and the tithe is not yours to waive.',
    'Souls from kills +8% and army damage +2% per level. The hero strikes 5% harder per level.',
    ItemSlot.Weapon,
    2,
    6,
    { soulGain: 0.08, unitDamage: 0.02 },
    { heroDamage: 0.05 },
  ),
  it(
    'hollow_lance',
    'The Hollow Lance',
    'Light enough for one hand. Nobody who has held it has ever needed the other.',
    'Cavalry charge damage +9% per level. The hero strikes 5% harder per level.',
    ItemSlot.Weapon,
    2,
    8,
    { cavalryCharge: 0.09 },
    { heroDamage: 0.05 },
  ),
  it(
    'bell_hammer',
    'Bell-Hammer',
    'Cast from the crown of the ninth bell. It rings once per skull and is otherwise silent.',
    'Army damage +5% and power damage +4% per level. The hero strikes 9% harder per level.',
    ItemSlot.Weapon,
    3,
    14,
    { unitDamage: 0.05, powerDamage: 0.04 },
    { heroDamage: 0.09 },
  ),
  it(
    'thirsting_edge',
    'The Thirsting Edge',
    'It heals whoever is holding it, out of whoever is holding the other end of the wound.',
    'Your units heal 1.2% of damage dealt and hit 3% harder, per level. The hero strikes 8% harder per level.',
    ItemSlot.Weapon,
    4,
    22,
    { lifesteal: 0.012, unitDamage: 0.03 },
    { heroDamage: 0.08 },
  ),

  // ── Armour ───────────────────────────────────────────────────────────────
  it(
    'ash_fouled_plate',
    'Ash-Fouled Plate',
    'Scoured once a year, on the wrong day, by somebody’s son.',
    'Army health +4% per level. The hero himself takes 7% less per level.',
    ItemSlot.Armour,
    1,
    1,
    { unitHp: 0.04 },
    { heroWard: 0.07 },
  ),
  it(
    'tanners_coat',
    'The Tanner’s Coat',
    'Leather that was, until quite recently, extremely motivated.',
    'Army health +2% and movement +2% per level. The hero takes 4% less per level.',
    ItemSlot.Armour,
    1,
    1,
    { unitHp: 0.02, moveSpeed: 0.02 },
    { heroWard: 0.04 },
  ),
  it(
    'mail_of_the_ninth',
    'Mail of the Ninth',
    'Nine rings deep at the throat and thinner everywhere the smith thought it mattered less.',
    'Siege damage to your structures −3% and army health +3% per level. The hero takes 6% less per level.',
    ItemSlot.Armour,
    2,
    7,
    { siegeResist: -0.03, unitHp: 0.03 },
    { heroWard: 0.06 },
  ),
  it(
    'bone_harness',
    'Bone Harness',
    'Somebody’s ribs, worn on the outside, which is agreed to be the wrong way round.',
    'Army health +3% per level, and the morale floor rises 2% per level.',
    ItemSlot.Armour,
    2,
    9,
    { unitHp: 0.03, moraleFloor: 0.02 },
    { heroWard: 0.06 },
  ),
  it(
    'drowned_cuirass',
    'Drowned Cuirass',
    'Pulled off a man who is still down there, and who is still, in his way, objecting.',
    'Army health +5% per level, and melee attackers take 1.6 damage back per level. The hero takes 10% less per level.',
    ItemSlot.Armour,
    3,
    16,
    { unitHp: 0.05, thorns: 1.6 },
    { heroWard: 0.1 },
  ),

  // ── Banner ───────────────────────────────────────────────────────────────
  it(
    'widows_rag',
    'The Widow’s Rag',
    'It has never been dropped. It has been carried by eleven people.',
    'The morale floor rises 3% per level, and veterancy accrues 5% faster per level.',
    ItemSlot.Banner,
    1,
    1,
    { moraleFloor: 0.03, veterancyRate: 0.05 },
  ),
  it(
    'black_pennon',
    'Black Pennon',
    'Black on account of what it was dipped in, not what it was dyed with.',
    'Army damage +3% and movement +3% per level.',
    ItemSlot.Banner,
    1,
    2,
    { unitDamage: 0.03, moveSpeed: 0.03 },
  ),
  it(
    'colours_of_the_sixth',
    'Colours of the Sixth',
    'All of the song survives. None of them did.',
    'Veterancy accrues 12% faster per level.',
    ItemSlot.Banner,
    2,
    5,
    { veterancyRate: 0.12 },
  ),
  it(
    'standing_roll',
    'The Standing Roll',
    'Most of it crossed out. The last few names written very small, to leave room.',
    'Population cap +4% and reinforcements 4% cheaper, per level.',
    ItemSlot.Banner,
    2,
    8,
    { popCap: 0.04, reinforceCost: -0.04 },
  ),
  it(
    'horn_of_the_late_watch',
    'Horn of the Late Watch',
    'One note. It has only ever been blown late.',
    'Army damage +3%, attack speed +3%, and the morale floor rises 2% — per level.',
    ItemSlot.Banner,
    3,
    15,
    { unitDamage: 0.03, attackSpeed: 0.03, moraleFloor: 0.02 },
  ),

  // ── Seal ─────────────────────────────────────────────────────────────────
  it(
    'tallymans_seal',
    'The Tallyman’s Seal',
    'Pressed into wax by a man who counted everything and trusted none of it.',
    'All production +5% per level.',
    ItemSlot.Seal,
    1,
    1,
    { resourceRate: 0.05 },
  ),
  it(
    'masons_chit',
    'The Masons’ Chit',
    'Entitles the bearer to a favour the guild has not yet agreed to grant.',
    'Buildings cost 3% less, finish 6% faster, and walls stand 4% longer — per level.',
    ItemSlot.Seal,
    1,
    2,
    { buildCost: -0.03, buildSpeed: 0.06, wallHp: 0.04 },
  ),
  it(
    'watch_wardens_tally',
    'The Watch-Warden’s Tally',
    'A notched stick recording every night nothing happened. It is very long.',
    'Towers fire 5% harder and reach 2% further, per level.',
    ItemSlot.Seal,
    1,
    3,
    { towerDamage: 0.05, towerRange: 0.02 },
  ),
  it(
    'sable_seal',
    'Seal of Sable Rations',
    'Nobody asks what is in it. Everybody eats it.',
    'Grain upkeep −4% per level.',
    ItemSlot.Seal,
    2,
    4,
    { grainUpkeep: -0.04 },
  ),
  it(
    'writ_of_the_long_shed',
    'Writ of the Long Shed',
    'Grants the bearer a shed. The shed is enormous and nobody can say where it is.',
    'Storage +90 per level.',
    ItemSlot.Seal,
    2,
    6,
    { storage: 90 },
  ),
  it(
    'quiet_purse',
    'Seal of the Quiet Purse',
    'Every debt in the realm, itemised, in a hand that shakes badly toward the end.',
    'All production +6%, population cap +3%, storage +60 — per level.',
    ItemSlot.Seal,
    3,
    12,
    { resourceRate: 0.06, popCap: 0.03, storage: 60 },
  ),

  // ── Charm ────────────────────────────────────────────────────────────────
  it(
    'knucklebone',
    'Knucklebone on a Thong',
    'Absolutely nobody will explain it. It absolutely works.',
    'Army movement +4% and attack speed +2% per level.',
    ItemSlot.Charm,
    1,
    1,
    { moveSpeed: 0.04, attackSpeed: 0.02 },
  ),
  it(
    'gravewax_candle',
    'Gravewax Candle',
    'Rendered from the parts nobody asked to have back.',
    'Souls from kills +7% per level.',
    ItemSlot.Charm,
    1,
    3,
    { soulGain: 0.07 },
  ),
  it(
    'ashglass_loupe',
    'Ashglass Loupe',
    'Ground from a window that survived the burning, which everyone agrees is a bad sign.',
    'Archers gain 0.35 tiles and towers 2% range, per level.',
    ItemSlot.Charm,
    2,
    4,
    { archerRange: 0.35, towerRange: 0.02 },
  ),
  it(
    'vial_of_kept_ember',
    'Vial of Kept Ember',
    'Still warm. It has been still warm since before the Hearth was lit.',
    'Will regenerates 8% faster and powers deal 4% more, per level.',
    ItemSlot.Charm,
    2,
    6,
    { willRegen: 0.08, powerDamage: 0.04 },
  ),
  it(
    'carrion_token',
    'Carrion Token',
    'Signed by every crow in the realm. They have kept to it without exception.',
    'Each wave survived returns 2.5 more Will, per level.',
    ItemSlot.Charm,
    2,
    10,
    { willOnWaveEnd: 2.5 },
  ),
  it(
    'shard_of_the_ninth',
    'Shard of the Ninth Bell',
    'It hums when the wind is off the east, which it never is.',
    'Powers cool 3% faster and reach 4% wider, per level.',
    ItemSlot.Charm,
    3,
    13,
    { powerCooldown: -0.03, powerRadius: 0.04 },
  ),
  it(
    'unasked_pact',
    'The Unasked Pact',
    'Nobody signed it. Everybody keeps it. It is kept about the neck, on a cord.',
    'One in fifty of your fallen rises again, per level, and souls +5% per level.',
    ItemSlot.Charm,
    4,
    24,
    { corpseRise: 0.02, soulGain: 0.05 },
  ),
];

const BY_ID = new Map<string, ItemDef>();
for (const d of ITEMS) BY_ID.set(d.id, d);

export function itemDef(id: string): ItemDef | undefined {
  return BY_ID.get(id);
}

/** Everything that may drop at this wave, in catalogue order. */
export function itemsAvailable(wave: number): ItemDef[] {
  return ITEMS.filter((d) => d.minWave <= wave);
}
